'use client';

import { CircleLoader, CircleLoaderSize } from '@/components/ui/loaders/circle-loader/CircleLoader';
import { cn } from '@/lib/styleUtils';

interface ContentLoaderProps {
  /** Loader ring size. Defaults to `large` to match the list/detail loading UX. */
  size?: CircleLoaderSize;
  /** Extra classes for the centering wrapper (e.g. a fixed height on split panels). */
  className?: string;
  /** Test hook placed on the wrapper. Defaults to `"content-loader"`. */
  'data-cy'?: string;
}

/**
 * Canonical in-page loading indicator for My Account list/detail views.
 *
 * A single shared spinner so every data-loading surface looks identical (PB-3324):
 * pages render this while their backing fetch is in flight and only fall through to
 * an empty-state / not-found branch once the fetch has resolved. Mirrors the
 * centered-`CircleLoader` block that `active-parking` originally inlined.
 */
export function ContentLoader({
  size = CircleLoaderSize.large,
  className,
  'data-cy': dataCy = 'content-loader',
}: ContentLoaderProps) {
  return (
    <output
      aria-busy="true"
      aria-label="Loading"
      data-cy={dataCy}
      className={cn(
        'tw:flex tw:flex-1 tw:items-center tw:justify-center tw:w-full tw:py-massive',
        className
      )}
    >
      <CircleLoader size={size} />
    </output>
  );
}
